import { useEffect, useState } from "react";
import { X, Coffee, Stop, SignOut } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import PinPad from "./PinPad";
import TaskGrid, { PendingNoteDialog } from "./TaskGrid";
import { canSeeTask } from "../lib/roles";

export default function BreakDialog({ worker, onClose, prefilledPin = "" }) {
  const [status, setStatus] = useState(null); // { on_break, open_break }
  const [reasons, setReasons] = useState([]); // array of {name, category}
  const [openBreaks, setOpenBreaks] = useState([]);
  const [pendingTasks, setPendingTasks] = useState([]);
  const [flagReason, setFlagReason] = useState(null);
  const [flagBusy, setFlagBusy] = useState(false);
  const [reason, setReason] = useState("");
  const [customReason, setCustomReason] = useState("");
  const [endComment, setEndComment] = useState("");
  const [pin, setPin] = useState(prefilledPin);
  const [sending, setSending] = useState(false);
  const pinPrefilled = Boolean(prefilledPin);


  useEffect(() => {
    let alive = true;
    Promise.all([
      api.get(`/workers/${worker.id}/status`).then((r) => r.data),
      api.get("/settings/public").catch(() => ({ data: {} })).then((r) => r.data || {}),
    ]).then(([st]) => {
      if (!alive) return;
      setStatus(st);
    });
    api
      .get("/settings/public/break-reasons")
      .then((r) => {
        if (!alive) return;
        const fams = Object.fromEntries((r.data.families || []).map((f) => [f.id, f]));
        const arr = (r.data.reasons || [])
          .map((x) => {
            if (typeof x === "string") return { name: x, category: "ocio", visible_roles: ["todos"], requires_pin: true };
            const fam = fams[x?.family_id];
            return {
              name: x?.name || "",
              category: x?.category || "ocio",
              visible_roles: x?.visible_roles || ["todos"],
              requires_pin: x?.requires_pin !== false,
              color: fam?.color,
              family_name: fam?.name || "",
            };
          })
          .filter((x) => x.name && canSeeTask(worker, x))
          .sort((a, b) => a.name.localeCompare(b.name, "es", { sensitivity: "base" }));
        setReasons(arr);
      })
      .catch(() => {
        if (alive) setReasons([
          { name: "Fumar", category: "ocio", requires_pin: true },
          { name: "Otros", category: "ocio", requires_pin: true },
          { name: "Trabajo almacén", category: "ocio", requires_pin: true },
        ]);
      });
    api.get("/breaks/open").then((r) => { if (alive) setOpenBreaks(r.data || []); }).catch(() => {});
    api.get("/tasks/pending").then((r) => { if (alive) setPendingTasks(r.data || []); }).catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [worker.id]);

  const reloadPending = () => api.get("/tasks/pending").then((r) => setPendingTasks(r.data || [])).catch(() => {});

  const onFlag = async (name, isPending) => {
    if (!isPending) { setFlagReason(name); return; }
    try {
      await api.delete(`/tasks/pending/${encodeURIComponent(name)}`);
      toast.success("Tarea ya no está pendiente");
      reloadPending();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const confirmFlag = async (note) => {
    setFlagBusy(true);
    try {
      await api.post("/tasks/pending", { reason: flagReason, note, worker_id: worker.id });
      toast.success("Tarea marcada como pendiente");
      setFlagReason(null);
      reloadPending();
    } catch (err) { toast.error(formatApiError(err)); } finally { setFlagBusy(false); }
  };

  const submitStart = async (e) => {
    e.preventDefault();
    if (!reason) { toast.error("Selecciona una tarea"); return; }
    const selected = reasons.find((r) => r.name === reason);
    const needsPin = !selected || selected.requires_pin !== false || !status?.is_clocked_in;
    if (needsPin && !pin) { toast.error("Introduce tu contraseña"); return; }
    setSending(true);
    try {
      await api.post(`/workers/${worker.id}/break/start`, {
        pin,
        reason,
        custom_reason: reason === "Otros" ? customReason : "",
      });
      toast.success("Tarea iniciada");
      onClose(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const submitStop = async (e) => {
    e.preventDefault();
    const openReason = reasons.find((r) => r.name === status?.open_break?.reason);
    const needsPin = !openReason || openReason.requires_pin !== false;
    if (needsPin && !pin) { toast.error("Introduce tu contraseña"); return; }
    setSending(true);
    try {
      await api.post(`/workers/${worker.id}/break/stop`, { pin, comment: endComment });
      toast.success("Tarea finalizada");
      onClose(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  if (!status) return null;
  const onBreak = status.on_break;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => !sending && onClose(false)}>
      <form
        onSubmit={onBreak ? submitStop : submitStart}
        onClick={(e) => e.stopPropagation()}
        className={`bg-white rounded-2xl w-full ${onBreak ? "max-w-sm" : "max-w-3xl"} p-6 shadow-2xl max-h-[95vh] overflow-y-auto`}
        data-testid="break-dialog"
      >
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${onBreak ? "bg-red-500" : "bg-amber-500"}`}>
              {onBreak ? <Stop size={22} weight="fill" color="#fff" /> : <Coffee size={22} weight="duotone" color="#fff" />}
            </div>
            <div>
              <div className="label-cap">{onBreak ? "en tarea" : "iniciar tarea"}</div>
              <h3 className="font-display font-bold text-lg">{worker.name}</h3>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => onClose(false)}
              disabled={sending}
              data-testid="break-exit"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-[var(--border)] font-display font-semibold text-sm hover:bg-[var(--bg)] transition-colors"
            >
              <SignOut size={16} weight="bold" /> Salir
            </button>
            <button type="button" onClick={() => onClose(false)} disabled={sending} className="p-1 rounded hover:bg-[var(--bg)]" data-testid="break-close">
              <X size={18} />
            </button>
          </div>
        </div>

        {onBreak ? (
          <div className="space-y-3">
            <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-sm">
              <div className="label-cap mb-1">motivo</div>
              <div className="font-display font-semibold">
                {status.open_break?.reason}{status.open_break?.reason === "Otros" && status.open_break?.custom_reason ? ` — ${status.open_break.custom_reason}` : ""}
              </div>
              <div className="text-xs text-[var(--text-secondary)] mt-1">
                Desde: {new Date(status.open_break?.start_time).toLocaleString("es-ES")}
              </div>
            </div>
            <div>
              <label className="label-cap block mb-1">Comentario (opcional)</label>
              <textarea
                value={endComment}
                onChange={(e) => setEndComment(e.target.value)}
                placeholder="¿Algo que anotar sobre esta tarea?"
                rows={2}
                maxLength={400}
                data-testid="break-end-comment"
                className="w-full px-3 py-2 rounded-xl border border-[var(--border)] bg-white text-sm"
              />
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <label className="label-cap block mb-2">Elige la tarea</label>
              <TaskGrid reasons={reasons} openBreaks={openBreaks} pendingTasks={pendingTasks} selected={reason} onSelect={setReason} onFlag={onFlag} />
            </div>
            {reason === "Otros" && (
              <div>
                <label className="label-cap block mb-1">Detalle</label>
                <input
                  type="text"
                  value={customReason}
                  onChange={(e) => setCustomReason(e.target.value)}
                  placeholder="Explica el motivo…"
                  data-testid="break-custom-reason"
                  className="w-full px-3 py-2 rounded-xl border border-[var(--border)] bg-white"
                  required
                />
              </div>
            )}
          </div>
        )}

        {(() => {
          const selected = reasons.find((r) => r.name === reason);
          const openReason = reasons.find((r) => r.name === status?.open_break?.reason);
          const pinSkippableNow = onBreak
            ? openReason && openReason.requires_pin === false
            : selected && selected.requires_pin === false && status?.is_clocked_in;
          const showPinPad = !pinPrefilled && !pinSkippableNow && (onBreak || Boolean(reason));
          const submitDisabled = sending
            || (!onBreak && !reason)
            || (!pinSkippableNow && !pin)
            || (reason === "Otros" && !customReason && !onBreak);
          return (
            <>
              {showPinPad && (
                <div className={`mt-4 ${onBreak ? "" : "max-w-sm mx-auto"}`}>
                  <label className="label-cap block mb-2">Contraseña</label>
                  <PinPad
                    value={pin}
                    onChange={setPin}
                    maxLength={11}
                    testIdPrefix="break-pinpad"
                    inputTestId="break-pin"
                    placeholder="••••"
                  />
                </div>
              )}

              {pinSkippableNow && !pinPrefilled && (
                <div className="mt-4 rounded-xl bg-emerald-50 border border-emerald-200 p-3 text-xs text-emerald-700" data-testid="break-pin-skipped">
                  Esta tarea no requiere identificación. {onBreak ? "Pulsa para finalizarla directamente." : "Pulsa para iniciarla directamente."}
                </div>
              )}

              <div className="flex gap-2 mt-5">
                <button type="button" onClick={() => onClose(false)} disabled={sending} className="flex-1 py-3 rounded-xl border border-[var(--border)] font-display font-semibold">
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={submitDisabled}
                  data-testid="break-submit"
                  className={`flex-1 py-3 rounded-xl font-display font-bold text-white disabled:opacity-40 ${onBreak ? "bg-red-600 hover:bg-red-700" : "bg-amber-600 hover:bg-amber-700"}`}
                >
                  {sending ? "…" : onBreak ? "Finalizar tarea" : "Iniciar tarea"}
                </button>
              </div>
            </>
          );
        })()}
      </form>
      {flagReason && (
        <PendingNoteDialog reason={flagReason} busy={flagBusy} onCancel={() => setFlagReason(null)} onConfirm={confirmFlag} />
      )}
    </div>
  );
}
